import { createResource } from 'react-resource-router';

import { ResourceContext } from 'src/router/types';
import { urls } from 'src/urls/jira';
import { authRequest, jsonHeaders } from 'src/utils/fetch';

export type JiraSite = {
  type: 'jira-site';
  cloudId: string;
  cloudName: string;
  cloudUrl: string;
  connected: boolean;
  isRovoEnabled: boolean;
  isRovoDevAgentsEnabled: boolean;
};

export type JiraSitesResponse = {
  size: number;
  page: number;
  pageLen: number;
  values: JiraSite[];
};

const PAGE_LEN = 100;

export const jiraSitesResource = createResource({
  type: 'JIRA_SITES',
  getKey: routerStoreContext => {
    const { workspaceSlug, repositoryOwner } = routerStoreContext.match.params;
    return `jira-sites:${workspaceSlug || repositoryOwner}`;
  },
  maxAge: 5 * 60 * 1000, // 5 minutes
  getData: async (
    _params,
    { csrftoken, reduxStore }: ResourceContext
  ): Promise<JiraSite[]> => {
    const { currentUser, defaultWorkspace } = reduxStore.getState().global;
    if (!currentUser || !defaultWorkspace?.uuid) {
      throw new Error('Workspace slug and account ID not found');
    }

    const fetchPage = async (page: number): Promise<JiraSitesResponse> => {
      const response = await fetch(
        authRequest(
          urls.api.internal.sitesWithRovoDevAgents(
            defaultWorkspace.uuid,
            currentUser,
            page,
            PAGE_LEN
          ),
          { headers: jsonHeaders },
          csrftoken
        )
      );

      if (!response.ok) {
        throw new Error(
          `Error fetching Jira sites (page ${page}): ${response.statusText}`
        );
      }

      return response.json();
    };

    const fetchAllPages = async (
      page: number = 1,
      accumulatedSites: JiraSite[] = []
    ): Promise<JiraSite[]> => {
      const data = await fetchPage(page);
      const allSites = [...accumulatedSites, ...data.values];

      if (allSites.length < data.size) {
        return fetchAllPages(page + 1, allSites);
      }

      return allSites;
    };

    try {
      return await fetchAllPages();
    } catch (error) {
      throw new Error(`Failed to fetch Jira sites: ${error.message}`);
    }
  },
});
